import React, { useEffect, useState } from 'react';
import universeService from '../../services/universeService';
import styles from './UniverseContextSelector.module.css';

const UniverseContextSelector = ({
  universes,
  universeId,
  elseworldId,
  onUniverseChange,
  onElseworldChange,
  entityLabel = 'personnage',
}) => {
  const [elseworlds, setElseworlds] = useState([]);
  const [loadingElseworlds, setLoadingElseworlds] = useState(false);

  useEffect(() => {
    const loadElseworlds = async () => {
      if (!universeId) {
        setElseworlds([]);
        return;
      }

      const universe = universes.find((item) => item.id === universeId);
      if (!universe?.slug) {
        setElseworlds([]);
        return;
      }

      try {
        setLoadingElseworlds(true);
        const data = await universeService.getUniverseElseworlds(universe.slug);
        setElseworlds(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching elseworlds:', err);
        setElseworlds([]);
      } finally {
        setLoadingElseworlds(false);
      }
    };

    loadElseworlds();
  }, [universeId, universes]);

  const handleUniverseChange = (value) => {
    onUniverseChange(value);
    onElseworldChange(null);
  };

  const selectedUniverse = universes.find((item) => item.id === universeId);

  return (
    <section className={styles.contextPanel} aria-labelledby="universe-context-title">
      <div className={styles.contextHeader}>
        <div className={styles.contextIcon} aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
        </div>
        <div>
          <h2 id="universe-context-title" className={styles.contextTitle}>
            Univers du {entityLabel}
          </h2>
          <p className={styles.contextSubtitle}>
            Choisissez l&apos;univers et, si besoin, une réalité alternative (elseworld).
          </p>
        </div>
      </div>

      <div className={styles.contextFields}>
        <div className={styles.contextField}>
          <label htmlFor="universe-context-universe">Univers *</label>
          <select
            id="universe-context-universe"
            value={universeId || ''}
            onChange={(e) => handleUniverseChange(e.target.value ? parseInt(e.target.value, 10) : null)}
          >
            <option value="">Sélectionner un univers</option>
            {universes.map((universe) => (
              <option key={universe.id} value={universe.id}>
                {universe.name}
              </option>
            ))}
          </select>
        </div>

        {universeId && (
          <div className={styles.contextField}>
            <label htmlFor="universe-context-elseworld">Elseworld</label>
            <select
              id="universe-context-elseworld"
              value={elseworldId || ''}
              onChange={(e) => onElseworldChange(e.target.value ? parseInt(e.target.value, 10) : null)}
              disabled={loadingElseworlds || elseworlds.length === 0}
            >
              <option value="">
                {loadingElseworlds
                  ? 'Chargement...'
                  : elseworlds.length === 0
                    ? 'Aucun elseworld disponible'
                    : 'Univers principal (aucun elseworld)'}
              </option>
              {elseworlds.map((elseworld) => (
                <option key={elseworld.id} value={elseworld.id}>
                  {elseworld.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {selectedUniverse && (
        <p className={styles.contextHint}>
          Ce {entityLabel} sera rattaché à <strong>{selectedUniverse.name}</strong>
          {elseworldId && elseworlds.find((item) => item.id === elseworldId)
            ? ` · Elseworld ${elseworlds.find((item) => item.id === elseworldId).name}`
            : ''}
          .
        </p>
      )}
    </section>
  );
};

export default UniverseContextSelector;
